import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnChanges, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { EAccess } from '@app/access/access.model';
import { LoginUser } from '@app/core/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';

import { EAccessMethod } from '@app/access/access.model';
import { AccessGuard } from '@app/access/access-guard';
import { AuthenticationService } from '@app/core/services/auth.service';
import { LanguageService } from '@app/core/services/language.service';
import { DEFAULT_PROFILE_IMAGE } from '@app/app.constant';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  standalone: false
})

/**
 * Topbar component
 */
export class TopbarComponent implements OnInit, OnChanges {

  element;
  cookieValue;
  flagvalue;
  countryName;
  valueset;

  public user: LoginUser;
  public Access = EAccess;
  public AccessMethodKey = EAccessMethod;
  public imageUrl: string;

  private subs = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    public _cookiesService: CookieService,
    public languageService: LanguageService,
    public translate: TranslateService,
    private authService: AuthenticationService,
  ) {
    this.getCurrentUser();
  }

  listLang = [
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
  ];

  openMobileMenu: boolean;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  ngOnInit() {
    this.openMobileMenu = false;
    this.element = document.documentElement;

    this.cookieValue = this._cookiesService.get('lang');
    const val = this.listLang.filter(x => x.lang === this.cookieValue);
    this.countryName = val.map(element => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) { this.valueset = 'assets/images/flags/us.jpg'; }
    } else {
      this.flagvalue = val.map(element => element.flag);
    }
  }

  ngOnChanges() {
    this.getCurrentUser();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /** The licence's own logo when it has one, else Evide's asset of that name (logo, logo-dark, logo-light...). */
  brandLogo(asset: string): string {
    return this.user?.licenseLogoUrl || `assets/images/${asset}.png`;
  }

  get hasLicenceLogo(): boolean {
    return !!this.user?.licenseLogoUrl;
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  getCurrentUser() {
    const sub1 = this.authService.currentUserObs.subscribe((user) => {
      if (user) {
        this.user = user;
        this.imageUrl = user.imageUrl;
        if (!this.imageUrl) {
          this.imageUrl = DEFAULT_PROFILE_IMAGE;
        }
        this.ensureLicenseSwitcherAccess();
      }
    });
    this.subs.add(sub1);
  }

  // The licence/company switcher is gated by *accessVisibility on Authentication/ChangeLicense,
  // which reads AccessGuard.accessMethods[Authentication]. That static map is populated lazily by
  // route guards, and only the app-root layout + dashboard routes request the Authentication
  // controller — no licence-area route does. So once that entry is lost (a token refresh after
  // idle empties it) while sitting on the licence page, nothing reloads it and the switcher stays
  // gone until a hard refresh. The topbar is the persistent owner of the switcher (the switcher
  // component itself isn't instantiated while the directive hides it, so it can't self-heal), so
  // it loads its own permission whenever it's missing — on init and on every currentUser re-emit.
  // Token refreshes re-emit currentUser, so this recovers without a manual refresh. The fetch is
  // coalesced + cached in the auth service, so repeated emits don't cause duplicate calls.
  private ensureLicenseSwitcherAccess(): void {
    if (AccessGuard.accessMethods[EAccess.Authentication]?.length) {
      return;
    }
    const sub = this.authService
      .getPermitedMultiControllerMethodsCached([EAccess.Authentication])
      .subscribe({
        next: (res) => {
          res?.forEach((item) => {
            AccessGuard.accessMethods[item.controllerName as EAccess] = item.permitedMethods || [];
          });
        },
        // Swallow failures (e.g. a token still rotating) — the next currentUser emit retries.
        error: () => { },
      });
    this.subs.add(sub);
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Logout the user
   */
  logout() {
    this.authService.logout();
  }

  /**
   * Send the user into the B2C password-reset flow. Pen test EVI-003-013.
   */
  changePassword() {
    this.authService.changePassword();
  }

  /**
   * Fullscreen method
   */
  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement && !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        /* Firefox */
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        /* IE/Edge */
        this.document.msExitFullscreen();
      }
    }
  }
}
